// Real authentication service for BrandScope frontend
// Connects to Cloudflare D1 authentication API

import { User, LoginCredentials, RegisterData } from '../types/auth';

export interface AuthResponse {
  success: boolean;
  user?: User;
  accessToken?: string;
  refreshToken?: string;
  message?: string;
  error?: string;
  expiresIn?: number;
}

export interface AuthServiceError {
  success: false;
  error: string;
  retryAfter?: number;
}

export interface AuthServiceSuccess<T> {
  success: true;
  data: T;
  message?: string;
}

export type AuthServiceResult<T> = AuthServiceSuccess<T> | AuthServiceError;

class RealAuthService {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    // Determine API base URL
    this.baseUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:8788' 
      : window.location.origin;
    
    // Initialize tokens from localStorage
    this.loadTokensFromStorage();
  }

  /**
   * Register a new user account
   */
  async register(data: RegisterData): Promise<AuthServiceResult<User>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Registration failed',
          retryAfter: result.retryAfter
        };
      }

      return {
        success: true,
        data: result.user,
        message: result.message
      };

    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: 'Network error during registration'
      };
    }
  }

  /**
   * Login user and store tokens
   */
  async login(credentials: LoginCredentials): Promise<AuthServiceResult<User>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Login failed',
          retryAfter: result.retryAfter
        };
      }

      // Store tokens
      this.accessToken = result.accessToken;
      this.refreshToken = result.refreshToken;
      this.saveTokensToStorage();

      return {
        success: true,
        data: result.user,
        message: result.message
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Network error during login'
      };
    }
  }

  /**
   * Logout user and clear tokens
   */
  async logout(): Promise<AuthServiceResult<boolean>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.accessToken && { 'Authorization': `Bearer ${this.accessToken}` })
        },
        credentials: 'include',
        body: JSON.stringify({
          refreshToken: this.refreshToken
        }),
      });

      // Clear tokens regardless of response
      this.clearTokens();

      const result = await response.json();

      return {
        success: true,
        data: true,
        message: result.message || 'Logged out successfully'
      };

    } catch (error) {
      console.error('Logout error:', error);
      // Still clear tokens on error
      this.clearTokens();
      
      return {
        success: true,
        data: true,
        message: 'Logged out successfully'
      };
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<AuthServiceResult<User>> {
    try {
      // Ensure we have a valid access token
      const hasValidToken = await this.ensureValidToken();
      
      if (!hasValidToken) {
        return {
          success: false,
          error: 'Not authenticated'
        };
      }

      const response = await fetch(`${this.baseUrl}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        // If token is invalid, try to refresh
        if (response.status === 401) {
          const refreshed = await this.refreshTokens();
          if (refreshed) {
            // Retry with new token
            return this.getCurrentUser();
          }
        }

        return {
          success: false,
          error: result.error || 'Failed to get user information'
        };
      }

      return {
        success: true,
        data: result.user
      };

    } catch (error) {
      console.error('Get current user error:', error);
      return {
        success: false,
        error: 'Network error while getting user information'
      };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshTokens(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;

    return result;
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      if (!this.refreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          refreshToken: this.refreshToken
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Refresh token is invalid, clear all tokens
        this.clearTokens();
        return false;
      }

      // Update tokens
      this.accessToken = result.accessToken;
      this.refreshToken = result.refreshToken;
      this.saveTokensToStorage();

      return true;

    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearTokens();
      return false;
    }
  }

  /**
   * Ensure we have a valid access token
   */
  private async ensureValidToken(): Promise<boolean> {
    if (!this.accessToken) {
      return false;
    }

    // Check if token is expired (basic check)
    try {
      const tokenParts = this.accessToken.split('.');
      if (tokenParts.length !== 3) {
        return await this.refreshTokens();
      }

      const payload = JSON.parse(atob(tokenParts[1] || ''));
      const now = Math.floor(Date.now() / 1000);
      
      // If token expires in less than 1 minute, refresh it
      if (payload.exp && payload.exp - now < 60) {
        return await this.refreshTokens();
      }

      return true;
    } catch (error) {
      // If we can't decode the token, try to refresh
      return await this.refreshTokens();
    }
  }

  /**
   * Make authenticated API request
   */
  async authenticatedRequest(
    url: string, 
    options: RequestInit = {}
  ): Promise<Response> {
    // Ensure we have a valid token
    const hasValidToken = await this.ensureValidToken();
    
    if (!hasValidToken) {
      throw new Error('Not authenticated');
    }

    // Add authorization header
    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${this.accessToken}`);
    headers.set('Content-Type', 'application/json');

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    // If unauthorized, try to refresh token once
    if (response.status === 401) {
      const refreshed = await this.refreshTokens();
      
      if (refreshed) {
        // Retry with new token
        headers.set('Authorization', `Bearer ${this.accessToken}`);
        return fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });
      }
    }

    return response;
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.refreshToken;
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Load tokens from localStorage
   */
  private loadTokensFromStorage(): void {
    try {
      this.accessToken = localStorage.getItem('brandscope_access_token');
      this.refreshToken = localStorage.getItem('brandscope_refresh_token');
    } catch (error) {
      console.error('Error loading tokens from storage:', error);
    }
  }

  /**
   * Save tokens to localStorage
   */
  private saveTokensToStorage(): void {
    try {
      if (this.accessToken) {
        localStorage.setItem('brandscope_access_token', this.accessToken);
      }
      if (this.refreshToken) {
        localStorage.setItem('brandscope_refresh_token', this.refreshToken);
      }
    } catch (error) {
      console.error('Error saving tokens to storage:', error);
    }
  }

  /**
   * Clear all tokens
   */
  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    
    try {
      localStorage.removeItem('brandscope_access_token');
      localStorage.removeItem('brandscope_refresh_token');
      localStorage.removeItem('brandscope_user');
      localStorage.removeItem('brandscope_session');
    } catch (error) {
      console.error('Error clearing tokens from storage:', error);
    }
  }
}

// Export singleton instance
export const realAuthService = new RealAuthService();